import { useEffect, useState } from "react"
import FormErrors from "components/formParts/formErrors"
import Callout from "components/callout/callout"
import { useTranslation } from "react-i18next"
import { LocationWrite, InputMaxLength } from "types"
import TextInput from "components/inputs/textInput"
import TextAreaInput from "components/inputs/textareaInput"
import { logger } from "utils/secureLogger"
import useLocations from "hooks/useLocations"
import useLayout from "hooks/useLayout"
import Form from "components/forms/form"
import LocationSelector from "components/inputs/locationSelector"
import Button from "components/inputs/button"
import Section from "components/layout/section"
import { ReactComponent as SaveIcon } from "assets/icons/save.svg"
import ProcessingButton from "components/inputs/processingButton"
import { decodeFirebaseError } from "utils/firebaseHelpers"

interface EditLocationFormProps {
    storageId: string
    locationId: string
}

const EditLocationForm: React.FC<EditLocationFormProps> = ({ storageId, locationId }) => {

    const { t } = useTranslation()

    const { addToast } = useLayout()

    const [name, setName] = useState(``)
    const [description, setDescription] = useState(``)
    const [notes, setNotes] = useState(``)

    const [errors, setErrors] = useState<string[]>([]);

    const {
        location,
        allLocations,
        locationStatus,
        updateLocationAsync,
        isUpdating
    } = useLocations({ storageId, locationId })
        
    const [parentLocation, setParentLocation] = useState<string>(`_`)

    useEffect(() => {
        if (!location) return
        setName(location.name)
        setDescription(location.description)
        setNotes(location.notes || ``)
        setParentLocation(location.relations?.parentLocation || `_`)
    }, [location])

    const handleSubmit = async (event: React.FormEvent) => {
        logger.debug('Edit location form submitted')
        setErrors([])
        event.preventDefault()
        if (storageId === undefined) return
        
        try {
            const data: LocationWrite = {
                ids: {
                    storageId: storageId,
                    locationId: locationId,
                },
                _version: 1,
                name: name,
                description: description,
                notes: notes,
                relations: {
                    parentLocation: parentLocation === `_` ? undefined : parentLocation
                },
                isDeleted: false,
            }
            await updateLocationAsync(data)
            logger.info('Location updated successfully', { locationId }, 'EditLocation')
            addToast({ text: t('toastLocationUpdated'), colorScheme: 'green' })
        } catch (error: unknown) {
            const errorMessage = decodeFirebaseError(error instanceof Error ? error.message : String(error))
            setErrors([errorMessage])
            addToast({ text: t('toastLocationError'), colorScheme: 'red' })
            logger.error('Failed to update location', error)
        }
    }

    if (locationStatus === `loading`) return <Callout type={`neutral`} loadingIcon={true}>{t('loadingStorage')}</Callout>
    if (!location && locationStatus === `error`) return <Callout type={`error`}>{t('failedToLoadStorage')}</Callout>

    return (
        <Form
            className={`group`}
            onSubmit={handleSubmit}
            isLoading={isUpdating}
        >
            <Section>
                <TextInput id={`name`} label={t('labelLocationName')} required={true} value={name} onChange={(e) => setName(e.target.value)} maxLength={InputMaxLength.short} />
                <TextAreaInput label={t('description')} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={InputMaxLength.long} />
                <TextAreaInput label={t('notes')} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={InputMaxLength.extraLong} />
                <div className={`input-holder-wide`}>
                    <label>{t('labelParentLocation')}</label>
                    <LocationSelector
                        locations={allLocations.filter((loc) => loc.id !== locationId)}
                        allLocations={allLocations}
                        selectedLocation={parentLocation}
                        setSelectedLocation={setParentLocation}
                        hiddenLocations={[locationId]}
                        allowCreate={false}
                    />
                </div>
            </Section>
            <Section>
                <div className={`input-holder`}>
                    {isUpdating ? (
                        <ProcessingButton design="primary" />
                    ) : (
                        <Button wideOnMobile type="submit" design="primary"><SaveIcon /><span>{t('saveChanges')}</span></Button>
                    )}
                </div>
                {errors.length > 0 && <FormErrors errors={errors} />}
            </Section>
        </Form>
    )

}

export default EditLocationForm
